/**
 * Staff cost and productivity (pack slide 16).
 *
 * Recurring staff cost against net revenue, and both per full-time equivalent, for the
 * months of the year to the payroll month against the same months a year earlier. A cost
 * and productivity measure: every payroll type is counted in full-time equivalents —
 * employees, independent contractors, interns and learners — which is the one kind of
 * measure where this page combines them, and the panel says so on its face.
 *
 * Money over a campus paying fewer than five people arrives withheld (null) with its
 * people count; net revenue is not about people and stays. A withheld cell shows a dash,
 * never a figure worked back from the total. A key the response leaves out — a cost ratio
 * for a year with no campus revenue — reads as not available for this date.
 */
import React, { useMemo, type ReactNode } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { readCampusNames } from './IcaGovernancePanel';
import { MetricTile, TileRow } from './MetricTile';
import { LegendSwatch, PanelShell, PanelSkeleton, PanelUnavailable } from './PanelShell';
import {
  ACCENT,
  AXIS,
  GRID,
  Def,
  fmtDay,
  fmtMonth,
  fmtNum,
  fmtPct,
  panelDateLabel,
  readExtras,
  type DefLookup,
  type PayCostCampus,
  type WorkforcePanels,
} from './apiContract';

interface StaffCostPanelProps {
  panels: WorkforcePanels | null;
  loading: boolean;
  def: DefLookup;
}

const CURRENT_COLOR = ACCENT;
const PRIOR_COLOR = '#8A94A6';

const MILLIONS = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const fmtMillions = (v: number) => MILLIONS.format(v / 1_000_000);
const fmtRand = (v: number) => `R${fmtNum(Math.round(v))}`;
/** "2026-03" -> "Mar". */
const shortMonth = (ym: string) => fmtMonth(ym).slice(0, 3);

type WindowMeasure =
  | 'staff_cost_pct_of_net_revenue'
  | 'net_revenue_per_fte'
  | 'staff_cost_per_fte'
  | 'avg_monthly_fte';

const TILES: { key: WindowMeasure; label: string; defKey: string; fmt: (v: number) => string }[] = [
  { key: 'staff_cost_pct_of_net_revenue', label: 'Staff cost % of net revenue', defKey: 'pay_cost_pct', fmt: fmtPct },
  { key: 'net_revenue_per_fte', label: 'Net revenue per FTE', defKey: 'pay_cost_revenue_per_fte', fmt: fmtRand },
  { key: 'staff_cost_per_fte', label: 'Staff cost per FTE', defKey: 'pay_cost_cost_per_fte', fmt: fmtRand },
  { key: 'avg_monthly_fte', label: 'Average monthly FTE', defKey: 'pay_cost_avg_fte', fmt: (v) => fmtNum(Math.round(v)) },
];

type CampusFigure = 'cost_pct' | 'prior_cost_pct' | 'net_revenue' | 'staff_cost';

// =============================================================================
// DEFINITIONS — server definitions win; these fill the keys it does not publish.
// =============================================================================

const defBody = (label: string, body: string, caveat?: string): ReactNode => (
  <div className="space-y-1.5">
    <p className="font-semibold text-white">{label}</p>
    <p>{body}</p>
    {caveat ? (
      <ul className="list-disc space-y-0.5 pl-3.5 text-[11px] text-amber-200/90">
        <li>{caveat}</li>
      </ul>
    ) : null}
  </div>
);

const LOCAL_DEFS: Record<string, ReactNode> = {
  pay_cost: defBody(
    'Staff cost and productivity',
    'Recurring staff cost (cost to company less bonus and commission, everyone paid) as a percentage of net revenue, and net revenue and staff cost per full-time equivalent, for the months of the year to the payroll month against the same months a year earlier.',
    'Every payroll type is counted, in full-time equivalents: employees, independent contractors, interns and learners.',
  ),
  pay_cost_pct: defBody(
    'Staff cost % of net revenue',
    'Recurring staff cost for everyone paid, as a percentage of net revenue, over the months shown.',
  ),
  pay_cost_revenue_per_fte: defBody(
    'Net revenue per FTE',
    'Net revenue over the months shown, divided by the average monthly full-time equivalents paid.',
  ),
  pay_cost_cost_per_fte: defBody(
    'Staff cost per FTE',
    'Recurring staff cost over the months shown, divided by the average monthly full-time equivalents paid.',
  ),
  pay_cost_avg_fte: defBody(
    'Average monthly FTE',
    'Full-time equivalents paid, added up over the months shown and divided by the number of months. Capped at one per person.',
  ),
  pay_cost_by_campus: defBody(
    'By campus',
    'Staff cost as a percentage of net revenue for each campus, largest revenue first. A campus billed under a second code is shown with its parent campus.',
    'National and shared services carry payroll but no campus revenue, so campus percentages do not average to the total.',
  ),
  pay_cost_monthly: defBody(
    'Month by month',
    'Staff cost as a percentage of net revenue in each month of the year to the payroll month, against the same month a year earlier.',
    'January runs on a different payroll cycle and its ratio is not comparable, so it is listed and not charted.',
  ),
  pay_cost_withheld: defBody(
    'Withheld',
    'Withheld to protect small groups: the campus paid fewer than five people in these months, so its staff cost and cost ratio are not shown. Net revenue stays, and the cost stays in the total.',
  ),
  pay_cost_not_available: defBody(
    'Not available for this date',
    'This figure is not available for the months shown — for example, a campus with no revenue a year earlier has no cost ratio for that year. It is not a zero.',
  ),
};

/** A dash that says what it stands for, to a screen reader and in its tooltip. */
const Dash: React.FC<{ def: DefLookup; k: string; text: string }> = ({ def, k, text }) => (
  <span
    className="inline-flex items-center justify-end text-gray-400"
    title={text.charAt(0).toUpperCase() + text.slice(1)}
  >
    <span aria-hidden="true">—</span>
    <span className="sr-only">{text}</span>
    <Def def={def} k={k} />
  </span>
);

/** null: withheld. Absent: not available for this date. Never a figure worked back. */
const CampusCell: React.FC<{ row: PayCostCampus; field: CampusFigure; def: DefLookup }> = ({
  row,
  field,
  def,
}) => {
  const v = row[field];
  if (v === undefined) return <Dash def={def} k="pay_cost_not_available" text="not available for this date" />;
  if (v === null) return <Dash def={def} k="pay_cost_withheld" text="withheld to protect small groups" />;
  return <>{field === 'cost_pct' || field === 'prior_cost_pct' ? fmtPct(v) : fmtMillions(v)}</>;
};

/** "Pretoria (PT, PT2)" — a campus billed under two codes names both. */
function campusLabel(row: PayCostCampus, names: Record<string, string>): ReactNode {
  const codes = Array.isArray(row.codes) && row.codes.length ? row.codes : [row.campus];
  const name = names[row.campus];
  if (!name && codes.length === 1) return row.campus;
  return (
    <>
      {name ?? row.campus} <span className="text-gray-400">({codes.join(', ')})</span>
    </>
  );
}

export const StaffCostPanel: React.FC<StaffCostPanelProps> = ({ panels, loading, def }) => {
  const d = useMemo<DefLookup>(() => (k: string) => def(k) ?? LOCAL_DEFS[k], [def]);
  const names = useMemo(() => readCampusNames(panels), [panels]);
  const cost = readExtras(panels, 'pay_cost');

  const campuses = Array.isArray(cost?.by_campus) ? cost.by_campus.filter(Boolean) : [];
  const national = Array.isArray(cost?.cost_without_campus_revenue)
    ? cost.cost_without_campus_revenue.filter(Boolean)
    : [];
  const monthly = Array.isArray(cost?.monthly) ? cost.monthly.filter(Boolean) : [];
  // January is listed, not charted: its payroll cycle makes the ratio incomparable.
  const charted = monthly
    .filter((m) => !/-01$/.test(m.month))
    .map((m) => ({
      month: m.month,
      current: isNum(m.cost_pct) ? m.cost_pct : null,
      prior: isNum(m.prior_cost_pct) ? m.prior_cost_pct : null,
    }));
  const january = monthly.find((m) => /-01$/.test(m.month)) ?? null;
  const anyWithheld = [...campuses, ...national].some((r) => r.suppressed);
  const year = cost?.current?.to?.slice(0, 4) ?? null;
  const priorYear = cost?.prior?.to?.slice(0, 4) ?? null;
  const notAvailable = <Dash def={d} k="pay_cost_not_available" text="not available for this date" />;

  const windowNote = cost
    ? `${panelDateLabel(panels, 'pay_cost')} · ${fmtDay(cost.window?.from)} – ${fmtDay(cost.window?.to)} against ${fmtDay(cost.prior_window?.from)} – ${fmtDay(cost.prior_window?.to)}. Everyone paid, every payroll type, in full-time equivalents. Independent of the basis toggle and the period picker.`
    : 'Staff cost against net revenue, the year to the payroll month against a year earlier. Independent of the basis toggle and the period picker.';

  return (
    <PanelShell
      title="Staff cost and productivity"
      defKey="pay_cost"
      def={d}
      window={windowNote}
      footnote={
        cost ? (
          <>
            Staff cost is recurring cost to company less bonus and commission, for everyone paid in
            every payroll type — employees, independent contractors, interns and learners — so it is
            not an employee-only figure. The figures per FTE divide by the average monthly full-time
            equivalents paid over the same months. A campus billed under a second code is shown with
            its parent campus. Campus percentages do not average to the total, because national and
            shared services carry cost but no campus revenue.
            {anyWithheld
              ? ' A campus paying fewer than five people has its staff cost and cost ratio withheld to protect small groups, with one more campus where needed so it cannot be worked out from the total; its net revenue stays, and its cost stays in the total.'
              : ''}
          </>
        ) : null
      }
    >
      {loading && !panels ? (
        <PanelSkeleton height={320} />
      ) : !panels ? (
        <PanelUnavailable what="Staff cost and productivity" />
      ) : !cost ? (
        <div className="rounded border border-gray-100 bg-gray-50 p-2.5 text-[11px] leading-snug text-gray-500">
          Staff cost and productivity is not available for this date.
        </div>
      ) : (
        <div className="space-y-5">
          <TileRow min="160px">
            {TILES.map((t) => {
              const now = cost.current?.[t.key];
              const before = cost.prior?.[t.key];
              return (
                <MetricTile
                  key={t.key}
                  label={t.label}
                  value={isNum(now) ? t.fmt(now) : notAvailable}
                  sub={`a year earlier ${isNum(before) ? t.fmt(before) : 'not available'}`}
                  defKey={t.defKey}
                  def={d}
                  size="sm"
                />
              );
            })}
          </TileRow>

          {/* ── by campus ─────────────────────────────────────────────────── */}
          <div>
            <div className="mb-1.5 flex items-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
              By campus — largest revenue first
              <Def def={d} k="pay_cost_by_campus" />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="border-b text-gray-500">
                  <tr>
                    <th className="px-2 py-1.5 text-left font-medium">Campus</th>
                    <th className="px-2 py-1.5 text-right font-medium">
                      Cost % of revenue{year ? `, ${year}` : ''}
                    </th>
                    <th className="px-2 py-1.5 text-right font-medium">{priorYear ?? 'A year earlier'}</th>
                    <th className="px-2 py-1.5 text-right font-medium">Net revenue (R m)</th>
                    <th className="px-2 py-1.5 text-right font-medium">Staff cost (R m)</th>
                  </tr>
                </thead>
                <tbody>
                  {campuses.map((r) => (
                    <tr key={r.campus} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="px-2 py-1.5 text-gray-700">{campusLabel(r, names)}</td>
                      <td className="px-2 py-1.5 text-right font-medium tabular-nums text-gray-800">
                        <CampusCell row={r} field="cost_pct" def={d} />
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-gray-500">
                        <CampusCell row={r} field="prior_cost_pct" def={d} />
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-gray-700">
                        <CampusCell row={r} field="net_revenue" def={d} />
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-gray-700">
                        <CampusCell row={r} field="staff_cost" def={d} />
                      </td>
                    </tr>
                  ))}
                  {national.map((r) => (
                    <tr key={`national-${r.campus}`} className="border-b bg-gray-50/60 last:border-0">
                      <td className="px-2 py-1.5 text-gray-700">{campusLabel(r, names)}</td>
                      <td colSpan={3} className="px-2 py-1.5 text-right text-[11px] text-gray-500">
                        no campus revenue
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-gray-700">
                        <CampusCell row={r} field="staff_cost" def={d} />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2">
                    <td className="px-2 py-1.5 font-semibold text-heritage-blue">
                      All campuses and national services
                    </td>
                    <td className="px-2 py-1.5 text-right font-bold tabular-nums text-heritage-blue">
                      {isNum(cost.current?.staff_cost_pct_of_net_revenue)
                        ? fmtPct(cost.current.staff_cost_pct_of_net_revenue)
                        : notAvailable}
                    </td>
                    <td className="px-2 py-1.5 text-right font-semibold tabular-nums text-gray-600">
                      {isNum(cost.prior?.staff_cost_pct_of_net_revenue)
                        ? fmtPct(cost.prior.staff_cost_pct_of_net_revenue)
                        : notAvailable}
                    </td>
                    <td className="px-2 py-1.5 text-right font-bold tabular-nums text-heritage-blue">
                      {isNum(cost.current?.net_revenue) ? fmtMillions(cost.current.net_revenue) : notAvailable}
                    </td>
                    <td className="px-2 py-1.5 text-right font-bold tabular-nums text-heritage-blue">
                      {isNum(cost.current?.staff_cost) ? fmtMillions(cost.current.staff_cost) : notAvailable}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {national.length ? (
              <div className="mt-1.5 text-[11px] leading-snug text-gray-500">
                National and shared services carry payroll but no campus revenue; their cost is in the
                total, not in a campus ratio.
              </div>
            ) : null}
          </div>

          {/* ── month by month ────────────────────────────────────────────── */}
          {charted.length ? (
            <div>
              <div className="mb-1 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                <div className="flex items-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                  Staff cost % of net revenue, month by month
                  <Def def={d} k="pay_cost_monthly" />
                </div>
                <div className="flex items-center gap-3">
                  <LegendSwatch color={CURRENT_COLOR} label={year ?? 'This year'} defKey="pay_cost_monthly" def={d} />
                  <LegendSwatch
                    color={PRIOR_COLOR}
                    label={priorYear ?? 'A year earlier'}
                    defKey="pay_cost_monthly"
                    def={d}
                  />
                </div>
              </div>
              <div style={{ width: '100%', height: 200 }}>
                <ResponsiveContainer>
                  <LineChart data={charted} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                    <XAxis
                      dataKey="month"
                      tickFormatter={(v: string) => shortMonth(v)}
                      tick={{ fontSize: 11 }}
                      tickLine={false}
                      axisLine={{ stroke: AXIS }}
                    />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                      width={44}
                      domain={['auto', 'auto']}
                      tickFormatter={(v: number) => `${v}%`}
                    />
                    <RechartsTooltip
                      labelStyle={{ fontWeight: 600 }}
                      contentStyle={{ fontSize: 12 }}
                      labelFormatter={(v: string) => fmtMonth(v)}
                      formatter={(value: number | string, name: string) => [
                        typeof value === 'number' ? fmtPct(value) : 'not available for this month',
                        name,
                      ]}
                    />
                    <Line
                      type="monotone"
                      dataKey="current"
                      name={year ?? 'This year'}
                      stroke={CURRENT_COLOR}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      connectNulls={false}
                      isAnimationActive={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="prior"
                      name={priorYear ?? 'A year earlier'}
                      stroke={PRIOR_COLOR}
                      strokeWidth={2}
                      strokeDasharray="4 3"
                      dot={{ r: 3 }}
                      connectNulls={false}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              {january ? (
                <div className="mt-1 text-[11px] leading-snug text-gray-500">
                  {fmtMonth(january.month)}: {isNum(january.cost_pct) ? fmtPct(january.cost_pct) : 'not available'};{' '}
                  {fmtMonth(january.prior_month)}:{' '}
                  {isNum(january.prior_cost_pct) ? fmtPct(january.prior_cost_pct) : 'not available'}. January runs
                  on a different payroll cycle, so its ratio is listed here and not charted.
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
    </PanelShell>
  );
};
